import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Progress } from '@/components/ui/shadcn/progress';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import { AlertCircle, CheckCircle2, Clock, FileCode2, Loader2, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

interface ScanResult {
    tables: Record<string, number>;
    unknown_tables: string[];
    file_size_bytes: number;
    storage_path: string;
    file_name: string;
}

interface ImportStatus {
    status: 'pending' | 'running' | 'completed' | 'failed';
    progress: number;
    message: string | null;
    error: string | null;
    created_at: string | null;
    started_at: string | null;
    completed_at: string | null;
    metadata: { tables?: string[]; dry_run?: boolean; output?: string } | null;
}

const TABLE_LABELS: Record<string, string> = {
    users: 'Users',
    clients: 'Companies (clients)',
    export_presents: 'Export Projects',
    lithuania_presents: 'Local Projects',
    tasks: 'Tasks (Gantt)',
    links: 'Task Dependencies',
};

const POLL_INTERVAL_MS = 2000;

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
}

export default function LegacySqlImportEditor() {
    const { t } = useTranslations();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isScanning, setIsScanning] = useState(false);
    const [scanResult, setScanResult] = useState<ScanResult | null>(null);
    const [selectedTables, setSelectedTables] = useState<string[]>([]);
    const [dryRun, setDryRun] = useState(false);
    const [fixExportProjects, setFixExportProjects] = useState(false);
    const [cleanupLegacyRootWrappers, setCleanupLegacyRootWrappers] = useState(false);
    const [skipReconcile, setSkipReconcile] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [lastStatus, setLastStatus] = useState<ImportStatus | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current !== null) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, []);

    const fetchStatus = useCallback(async () => {
        try {
            const response = await fetch('/settings/system/legacy-sql-import/last-status', {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });
            const data = await response.json();
            if (data.legacy_sql_import) {
                const status = data.legacy_sql_import as ImportStatus;
                setLastStatus(status);
                if (status.status === 'completed' || status.status === 'failed') {
                    stopPolling();
                }
            }
        } catch {
            // silent
        }
    }, [stopPolling]);

    const startPolling = useCallback(() => {
        stopPolling();
        void fetchStatus();
        pollIntervalRef.current = setInterval(() => void fetchStatus(), POLL_INTERVAL_MS);
    }, [fetchStatus, stopPolling]);

    // Load last status on mount
    useEffect(() => {
        void fetchStatus();
    }, [fetchStatus]);

    // Resume polling on mount if a job is already running
    useEffect(() => {
        if (lastStatus && (lastStatus.status === 'running' || lastStatus.status === 'pending')) {
            startPolling();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // intentionally only on mount

    useEffect(() => {
        return () => stopPolling();
    }, [stopPolling]);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const ext = file.name.toLowerCase().split('.').pop();
        if (ext !== 'sql') {
            toast.error(t('toasts.settings_legacysqlimporteditor.select_sql_file', 'Please select a .sql file'));
            return;
        }

        if (file.size > 200 * 1024 * 1024) {
            toast.error(t('toasts.settings_legacysqlimporteditor.file_too_large', 'File must be under 200 MB'));
            return;
        }

        setSelectedFile(file);
        setScanResult(null);
        setSelectedTables([]);
    };

    const handleScan = async () => {
        if (!selectedFile) return;
        setIsScanning(true);

        try {
            const formData = new FormData();
            formData.append('sql_file', selectedFile);

            const response = await csrfFetch('/settings/system/legacy-sql-import/scan', {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message ?? 'Scan failed');
            }

            const result: ScanResult = data.scan;
            setScanResult(result);

            const nonEmpty = Object.entries(result.tables)
                .filter(([, count]) => count > 0)
                .map(([table]) => table);
            setSelectedTables(nonEmpty);

            toast.success(t('toasts.settings_legacysqlimporteditor.scan_success', 'SQL file scanned successfully'));
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to scan SQL file');
        } finally {
            setIsScanning(false);
        }
    };

    const toggleTable = (table: string) => {
        setSelectedTables((prev) => (prev.includes(table) ? prev.filter((t) => t !== table) : [...prev, table]));
    };

    const handleRun = async () => {
        if (!scanResult || selectedTables.length === 0) return;
        setIsRunning(true);

        try {
            const response = await csrfFetch('/settings/system/legacy-sql-import/run', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    storage_path: scanResult.storage_path,
                    tables: selectedTables,
                    dry_run: dryRun,
                    fix_export_projects: fixExportProjects,
                    cleanup_legacy_root_wrappers: cleanupLegacyRootWrappers,
                    skip_reconcile: skipReconcile,
                }),
            });

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message ?? 'Failed to start import');
            }

            toast.success(
                dryRun
                    ? t('toasts.settings_legacysqlimporteditor.dry_run_queued', 'Dry-run job queued. Check logs for output.')
                    : t('toasts.settings_legacysqlimporteditor.import_queued', 'Import job queued. Running in background.'),
            );

            setSelectedFile(null);
            setScanResult(null);
            setSelectedTables([]);
            if (fileInputRef.current) fileInputRef.current.value = '';

            startPolling();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to start import');
        } finally {
            setIsRunning(false);
        }
    };

    const isActive = lastStatus?.status === 'running' || lastStatus?.status === 'pending';

    const statusIcon = () => {
        if (!lastStatus) return null;
        if (lastStatus.status === 'completed') return <CheckCircle2 className="text-success-subtle-foreground h-4 w-4" />;
        if (lastStatus.status === 'failed') return <AlertCircle className="text-destructive h-4 w-4" />;
        if (lastStatus.status === 'running') return <Loader2 className="text-info-subtle-foreground h-4 w-4 animate-spin" />;
        return <Clock className="text-warning-subtle-foreground h-4 w-4" />;
    };

    return (
        <div className="space-y-4">
            {/* Internal badge */}
            <div className="flex items-center gap-2">
                <Badge variant="outline" className="border-warning-subtle bg-warning-subtle text-warning-subtle-foreground">
                    Internal use only
                </Badge>
                <span className="text-muted-foreground text-xs">
                    This module is specific to this deployment and is not needed for new installations.
                </span>
            </div>

            {/* File upload */}
            <div className="space-y-2">
                <input ref={fileInputRef} type="file" accept=".sql" onChange={handleFileSelect} className="hidden" id="legacy-sql-file" />

                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isScanning || isRunning || isActive}
                        className="h-8 px-3"
                    >
                        <Upload className="mr-1.5 h-3.5 w-3.5" />
                        {selectedFile ? 'Change file' : 'Select .sql file'}
                    </Button>

                    {selectedFile && (
                        <div className="text-muted-foreground flex min-w-0 flex-1 items-center gap-1 text-xs">
                            <FileCode2 className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{selectedFile.name}</span>
                            <span className="shrink-0">({formatBytes(selectedFile.size)})</span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setSelectedFile(null);
                                    setScanResult(null);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                }}
                                disabled={isScanning || isRunning}
                                className="h-6 w-6 p-0"
                            >
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    )}

                    {selectedFile && !scanResult && (
                        <Button onClick={handleScan} disabled={isScanning} size="sm" className="h-8 px-3">
                            {isScanning ? (
                                <>
                                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                    Scanning…
                                </>
                            ) : (
                                'Scan file'
                            )}
                        </Button>
                    )}
                </div>
            </div>

            {/* Scan results */}
            {scanResult && (
                <div className="space-y-3 rounded-md border p-3">
                    <div className="text-muted-foreground flex items-center justify-between text-xs">
                        <span className="text-foreground font-medium">{scanResult.file_name}</span>
                        <span>{formatBytes(scanResult.file_size_bytes)}</span>
                    </div>

                    <div className="space-y-1.5">
                        <p className="text-xs font-medium">Tables found — select which to import:</p>
                        <div className="grid grid-cols-2 gap-1.5">
                            {Object.entries(scanResult.tables).map(([table, count]) => (
                                <label
                                    key={table}
                                    className={`flex cursor-pointer items-center gap-2 rounded border px-2 py-1.5 text-xs transition-colors ${
                                        count === 0 ? 'text-muted-foreground opacity-50' : 'hover:bg-muted/50'
                                    } ${selectedTables.includes(table) ? 'border-primary bg-primary/5' : 'border-border'}`}
                                >
                                    <Checkbox
                                        checked={selectedTables.includes(table)}
                                        onCheckedChange={() => toggleTable(table)}
                                        disabled={count === 0}
                                        className="h-3.5 w-3.5"
                                    />
                                    <span className="min-w-0 flex-1 truncate">{TABLE_LABELS[table] ?? table}</span>
                                    <span className={`shrink-0 tabular-nums ${count === 0 ? '' : 'text-foreground font-medium'}`}>
                                        {count.toLocaleString()}
                                    </span>
                                </label>
                            ))}
                        </div>

                        {scanResult.unknown_tables.length > 0 && (
                            <p className="text-muted-foreground text-xs">Ignored tables: {scanResult.unknown_tables.join(', ')}</p>
                        )}
                    </div>

                    {/* Options */}
                    <div className="border-t pt-2">
                        <p className="mb-1.5 text-xs font-medium">Options</p>
                        <div className="grid grid-cols-2 gap-1.5">
                            {(
                                [
                                    { id: 'dry-run', label: 'Dry run (no changes)', value: dryRun, set: setDryRun },
                                    { id: 'skip-reconcile', label: 'Skip Gantt reconciliation', value: skipReconcile, set: setSkipReconcile },
                                    { id: 'fix-export', label: 'Fix export contract numbers', value: fixExportProjects, set: setFixExportProjects },
                                    {
                                        id: 'cleanup-wrappers',
                                        label: 'Cleanup root wrapper tasks',
                                        value: cleanupLegacyRootWrappers,
                                        set: setCleanupLegacyRootWrappers,
                                    },
                                ] as const
                            ).map(({ id, label, value, set }) => (
                                <label key={id} className="flex cursor-pointer items-center gap-2 text-xs">
                                    <Checkbox checked={value} onCheckedChange={(v) => set(Boolean(v))} className="h-3.5 w-3.5" />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <Button
                        onClick={handleRun}
                        disabled={isRunning || isActive || selectedTables.length === 0}
                        size="sm"
                        variant={dryRun ? 'outline' : 'default'}
                        className="w-full"
                    >
                        {isRunning ? (
                            <>
                                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                Starting…
                            </>
                        ) : dryRun ? (
                            'Run dry-run'
                        ) : (
                            'Run import'
                        )}
                    </Button>
                </div>
            )}

            {/* Job status + progress */}
            {lastStatus && (
                <div className="rounded-md border p-3 text-xs">
                    <div className="mb-2 flex items-center gap-2">
                        {statusIcon()}
                        <span className="font-medium capitalize">{lastStatus.status}</span>
                        {lastStatus.metadata?.dry_run && (
                            <Badge variant="outline" className="text-[10px]">
                                dry-run
                            </Badge>
                        )}
                        {isActive && <span className="text-muted-foreground ml-auto tabular-nums">{lastStatus.progress}%</span>}
                    </div>

                    {/* Progress bar — shown while active or just completed/failed */}
                    {(isActive || lastStatus.status === 'completed' || lastStatus.status === 'failed') && (
                        <div className="mb-2">
                            <Progress
                                value={lastStatus.progress}
                                className={`h-1.5 ${lastStatus.status === 'failed' ? '[&>[data-slot=progress-indicator]]:bg-destructive' : lastStatus.status === 'completed' ? '[&>[data-slot=progress-indicator]]:bg-success' : ''}`}
                            />
                        </div>
                    )}

                    {lastStatus.message && <p className="text-muted-foreground mt-1">{lastStatus.message}</p>}
                    {lastStatus.error && (
                        <div className="border-danger-subtle bg-danger-subtle text-danger-subtle-foreground mt-1 rounded border p-2">
                            <div className="flex items-start gap-1">
                                <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
                                <span className="line-clamp-3">{lastStatus.error}</span>
                            </div>
                        </div>
                    )}
                    <p className="text-muted-foreground mt-1">
                        {lastStatus.completed_at
                            ? `Completed: ${formatDate(lastStatus.completed_at)}`
                            : lastStatus.started_at
                              ? `Started: ${formatDate(lastStatus.started_at)}`
                              : `Queued: ${formatDate(lastStatus.created_at)}`}
                    </p>
                    {lastStatus.metadata?.tables && <p className="text-muted-foreground mt-0.5">Tables: {lastStatus.metadata.tables.join(', ')}</p>}
                    {lastStatus.metadata?.output && (
                        <details className="mt-2">
                            <summary className="text-muted-foreground hover:text-foreground cursor-pointer">Show output</summary>
                            <pre className="bg-muted mt-1 max-h-48 overflow-auto rounded p-2 text-[10px] whitespace-pre-wrap">
                                {lastStatus.metadata.output}
                            </pre>
                        </details>
                    )}
                </div>
            )}

            {!lastStatus && !scanResult && (
                <p className="text-muted-foreground text-xs">
                    Upload a legacy SQL dump to scan it. The scanner will identify which tables are present and how many rows each contains, then let
                    you choose what to import.
                </p>
            )}
        </div>
    );
}
